import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Circuit } from '@multirx/shared/types/track.js';
import type { CircuitPiece3dCatalogEntry } from '@multirx/shared/game/circuitPieceCatalog.js';
import { SURFACE_FILL_HEX } from '@multirx/shared/game/circuitSurfaceStyle.js';
import { fetchCircuitPieceCatalog } from './circuitPieceCatalogApi.js';
import { LIVE_CAR_PAIR_HALF_SPREAD } from './liveCarLaneOffset.js';
import type { Track3dTile } from './track3dLayout.js';
import {
  buildProceduralCorner,
  buildProceduralStraight,
  isProceduralCornerSlug,
  proceduralCornerArmFromSlug,
  proceduralCornerSlug,
  PROCEDURAL_ASPHALT_COLOR,
  PROCEDURAL_ROAD_WIDTH,
} from './proceduralRoadPieces.js';

/**
 * Construction de la scène statique du tracé (sol, herbe, éclairage, pièces de
 * route/bordures/ligne de départ), partagée entre l'aperçu 3D en lecture seule
 * (`circuit3dPreview.ts`, avec voitures live) et l'éditeur 3D (`circuit3dEditorScene.ts`,
 * sans voitures mais avec raycasting/placement). Ce module ne connaît ni caméra
 * ni contrôles ni boucle d'animation — uniquement "étant donné une scène et des
 * tuiles, peupler le décor".
 */

export const TILE_SIZE = 1;
const TRACK_MODEL_BASE = '/assets/circuits/models3d';

type DecorKey = 'straight';

/**
 * Slug catalogue par défaut pour une ligne droite sans pièce groupée explicite
 * (voir `Circuit.roadPieces`). Le virage par défaut n'a plus d'équivalent ici :
 * il est généré par calcul (voir `buildProceduralCorner`, arm=0), pas chargé
 * depuis un `.glb` — les modèles Kenney "roadCornerSmall/Large/Larger" ne
 * touchent jamais exactement le bord de case (mesuré pixel par pixel), un
 * défaut qui ne se voit qu'une fois plusieurs virages groupés bout à bout.
 */
const DEFAULT_TILE_SLUG: Record<DecorKey, string> = {
  straight: 'roadStraight',
};

/** Bordure violette des lignes droites du tour joker — même teinte que
 * `JOKER_LAP_BRANCH_COLOR` dans `circuit3dEditor.ts`. */
const JOKER_LAP_CURB_COLOR = 0x9b59b6;

/** Résout l'URL publique d'un slug via le catalogue ; repli sur la convention de nommage si absent. */
export function resolvePieceUrl(catalog: CircuitPiece3dCatalogEntry[], slug: string): string {
  return (
    catalog.find((entry) => entry.slug === slug)?.publicPath ?? `${TRACK_MODEL_BASE}/${slug}.glb`
  );
}

export interface DecorModelInfo {
  url: string;
  /** true pour un modèle voiture (voir `CircuitPiece3dCatalogEntry.carModel`) —
   * conservé ici pour reconstruire un `GhostModelPreview` sans reconsulter le
   * catalogue. */
  carModel: boolean;
  /** Empreinte au sol réelle (mètres/cases, après la mise à l'échelle "modèle
   * voiture" éventuelle) — axes du modèle avant rotation. */
  width: number;
  depth: number;
}

const decorModelInfoCache = new Map<string, Promise<DecorModelInfo>>();

/**
 * URL résolue + empreinte au sol réelle d'un modèle de décor catalogue, mises en
 * cache par slug — un modèle donné a toujours la même taille et le même
 * fichier, indépendamment de l'instance posée dans un circuit. Sert au collage
 * automatique entre décors dans l'éditeur (voir `circuit3dEditor.ts`), qui a
 * besoin à la fois de l'URL (aperçu fantôme) et du gabarit (calcul géométrique
 * du bord à accoler) sans reconsulter le catalogue à chaque survol.
 */
export function getDecorModelInfo(slug: string): Promise<DecorModelInfo> {
  const cached = decorModelInfoCache.get(slug);
  if (cached) return cached;
  const promise = fetchCircuitPieceCatalog()
    .then(async (catalog) => {
      const url = resolvePieceUrl(catalog, slug);
      const carModel = catalog.find((entry) => entry.slug === slug)?.carModel ?? false;
      const model = await loadModel(url);
      if (carModel) model.scale.setScalar(computeVehicleModelScale(model));
      const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3());
      return { url, carModel, width: size.x, depth: size.z };
    })
    .catch((err) => {
      // Un échec ne doit pas rester en cache indéfiniment (modèle temporairement
      // indisponible) — le prochain appel relance un vrai chargement.
      decorModelInfoCache.delete(slug);
      throw err;
    });
  decorModelInfoCache.set(slug, promise);
  return promise;
}

/** Tuile d'herbe (Kenney Nature Kit) — déjà centrée sur son origine, aucune correction requise. */
const GRASS_TILE_URL = `${TRACK_MODEL_BASE}/ground_grass.glb`;
/** Cases de marge autour du tracé couvertes par le sol en herbe 3D (tuiles Kenney). */
const GRASS_MARGIN = 8;
/**
 * Teinte herbe (Nature Kit retinté vert pastel) — base du sol lointain et des
 * tuiles Kenney (le `.glb` n'a qu'un `baseColorFactor` plat, sans nuances).
 */
export const GRASS_COLOR = 0xb8e09e;

const loader = new GLTFLoader();

export interface GrassAlbedoTextureOptions {
  size?: number;
  /** RNG injectable — tests déterministes ; défaut `Math.random`. */
  random?: () => number;
  color?: number;
}

export interface GrassTuftSpec {
  x: number;
  y: number;
  w: number;
  /** Multiplicateur de teinte (~0.55–1.35) pour nuances claires/sombres. */
  shade: number;
  alpha: number;
}

/**
 * Shade par case très léger (0.96–1.04) — casse à peine l'uniformité sans
 * effet « Minecraft » / damier. Le vrai grain vient des taches soft de la map.
 */
export function grassTileShade(gx: number, gy: number): number {
  const n = Math.sin(gx * 127.1 + gy * 311.7) * 43758.5453;
  const f = n - Math.floor(n);
  return 0.96 + f * 0.08;
}

/**
 * Grosses taches + grain fin. Les taches (rayon ~8–20 % de la texture) restent
 * lisibles de loin ; le grain 1–3 px ne survit pas au mipmapping seul.
 */
export function planGrassTufts(
  size: number,
  random: () => number = Math.random,
  count?: number
): GrassTuftSpec[] {
  const tufts: GrassTuftSpec[] = [];
  // Taches moyennes (organiques) — contraste modéré, pas de blocs saturés.
  const patchCount = Math.max(18, Math.round(size / 12));
  for (let i = 0; i < patchCount; i++) {
    tufts.push({
      x: random() * size,
      y: random() * size,
      w: size * (0.06 + random() * 0.12),
      shade: 0.78 + random() * 0.4,
      alpha: 0.22 + random() * 0.28,
    });
  }
  const n = count ?? Math.round(size * size * 0.025);
  for (let i = 0; i < n; i++) {
    tufts.push({
      x: random() * size,
      y: random() * size,
      w: 1.5 + random() * 4,
      shade: 0.8 + random() * 0.35,
      alpha: 0.2 + random() * 0.3,
    });
  }
  return tufts;
}

/**
 * Texture d'herbe procédurale (canvas) : taches larges + grain sur `GRASS_COLOR`.
 * Aussi utilisée comme base des tuiles Kenney (teinte par instance en plus).
 */
export function createGrassAlbedoTexture(options?: GrassAlbedoTextureOptions): THREE.CanvasTexture {
  const size = options?.size ?? 256;
  const random = options?.random ?? Math.random;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const base = new THREE.Color(options?.color ?? GRASS_COLOR);
    ctx.fillStyle = `#${base.getHexString()}`;
    ctx.fillRect(0, 0, size, size);
    const tuft = new THREE.Color();
    for (const spec of planGrassTufts(size, random)) {
      tuft.copy(base).multiplyScalar(spec.shade);
      const r = Math.max(1, spec.w / 2);
      const g = ctx.createRadialGradient(spec.x, spec.y, 0, spec.x, spec.y, r);
      const hex = `#${tuft.getHexString()}`;
      g.addColorStop(0, hex);
      g.addColorStop(1, `${hex}00`);
      ctx.globalAlpha = spec.alpha;
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(spec.x, spec.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

export function loadModel(url: string): Promise<THREE.Object3D> {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => resolve(gltf.scene),
      undefined,
      (err) => reject(err instanceof Error ? err : new Error(String(err)))
    );
  });
}

/**
 * Les pièces Kenney conservent une position héritée de la scène d'origine du kit
 * (décalage arbitraire, identique pour toutes les pièces). On calcule le décalage
 * qui centre une pièce (X/Z centrés, posée au sol) pour que la rotation en place
 * la garde dans sa case de grille.
 */
export function centerOffset(model: THREE.Object3D): THREE.Vector3 {
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  return new THREE.Vector3(-center.x, -box.min.y, -center.z);
}

export function anchorWithOffset(model: THREE.Object3D, offset: THREE.Vector3): THREE.Object3D {
  model.position.add(offset);
  const wrapper = new THREE.Group();
  wrapper.add(model);
  return wrapper;
}

/**
 * Facteur d'échelle qui ramène le plus grand côté horizontal (X ou Z) d'un modèle
 * .glb brut à une demi-case — même convention que les voitures en course (voir
 * `circuit3dPreview.ts#ensureCarPlacementMeta`). Sans ça, un modèle voiture posé
 * comme décor (voir `CircuitPiece3dCatalogEntry.carModel`) garde son échelle .glb
 * native, qui n'a aucune raison de correspondre à une case de circuit — les
 * pièces Kenney du kit décor, elles, sont déjà nativement à cette échelle.
 */
export function computeVehicleModelScale(model: THREE.Object3D): number {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  return (TILE_SIZE * 0.5) / Math.max(size.x, size.z, 0.001);
}

/**
 * Marquage au sol de la grille de départ : deux box de positionnement peints en
 * blanc (un par côté, gauche/droite, en retrait du bord et sans se toucher), chacun
 * ouvert vers l'arrière (les deux longs côtés + un seul petit côté, à l'avant — la
 * voiture "sort" par le côté manquant), posés sur la case de départ. Plus large que
 * long (les côtés gauche/droite sont les plus courts).
 */
function buildStartGridMarking(surfaceY: number): THREE.Group {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9 });
  const thickness = 0.045;
  // Hauteur du marquage (axe Y) : distincte de `thickness` (épaisseur du trait vu de
  // dessus) — faible pour à peine dépasser la route, comme une peinture au sol.
  const markingHeight = 0.012;
  // Plus grande dimension du box : réduite pour ne plus chevaucher son symétrique
  // (avec l'écart au centre actuel, l'ancienne valeur faisait se toucher les deux).
  const laneHalfWidth = 0.08;
  const laneOffsetX = LIVE_CAR_PAIR_HALF_SPREAD;
  const length = 0.08;

  const sideGeometry = new THREE.BoxGeometry(thickness, markingHeight, length);
  const closingGeometry = new THREE.BoxGeometry(
    laneHalfWidth * 2 + thickness,
    markingHeight,
    thickness
  );

  function buildLaneBox(centerX: number, y: number, z: number): THREE.Group {
    // Le box repose sur la route (base à `y`), pas centré dessus, pour rester
    // prévisible quelle que soit `markingHeight`.
    const restingY = y + markingHeight / 2;
    const box = new THREE.Group();
    const left = new THREE.Mesh(sideGeometry, material);
    left.position.set(centerX - laneHalfWidth, restingY, z);
    const right = new THREE.Mesh(sideGeometry, material);
    right.position.set(centerX + laneHalfWidth, restingY, z);
    // Fermé à l'avant, ouvert à l'arrière (demi-tour par rapport à la version précédente).
    const closing = new THREE.Mesh(closingGeometry, material);
    closing.position.set(centerX, restingY, z + length / 2);
    box.add(left, right, closing);
    return box;
  }

  // Le box de gauche est légèrement surélevé et poussé au bout de la case (effet
  // grille de départ échelonnée) ; celui de droite reste au milieu de la case.
  const leftLift = 0.03;
  const leftEndZ = -0.4;
  const rightMiddleZ = 0;
  group.add(
    buildLaneBox(-laneOffsetX, surfaceY + leftLift, leftEndZ),
    buildLaneBox(laneOffsetX, surfaceY, rightMiddleZ)
  );
  return group;
}

/** Lignes blanches de bord de chaussée sur une ligne droite — même repère local
 * que le modèle (avant la rotation.y d'instanciation, voir `buildStartGridMarking`
 * ci-dessus pour la même convention). */
function buildSideLines(surfaceY: number, roadHalfWidth = 0.42, lineWidth = 0.04): THREE.Group {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9 });
  const geometry = new THREE.BoxGeometry(lineWidth, 0.012, 1);
  const restingY = surfaceY + 0.006;
  const left = new THREE.Mesh(geometry, material);
  left.position.set(-roadHalfWidth, restingY, 0);
  const right = new THREE.Mesh(geometry, material);
  right.position.set(roadHalfWidth, restingY, 0);
  group.add(left, right);
  return group;
}

/** Le marquage au sol des pièces "ligne droite" est gravé perpendiculaire à la route. */
export const STRAIGHT_EXTRA_ROTATION = Math.PI / 2;

function tileRotationY(tile: Track3dTile): number {
  return -(tile.rotationSteps * Math.PI) / 2;
}

/** Groupe positionné/orienté comme la case (les enfants héritent de la rotation). */
export function createTileGroup(tile: Track3dTile): THREE.Group {
  const group = new THREE.Group();
  group.position.set((tile.x + 0.5) * TILE_SIZE, 0, (tile.y + 0.5) * TILE_SIZE);
  group.rotation.y = tileRotationY(tile);
  return group;
}

export interface TrackSceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  centerX: number;
  centerZ: number;
  span: number;
}

/**
 * Étendue du tracé (toutes tuiles confondues) — sert à cadrer caméra/brouillard/sol
 * lointain. `fallbackGrid` cadre sur la grille entière quand il n'y a encore aucune
 * tuile (circuit vide, tracé pas encore commencé en 3D) plutôt que sur un span
 * minimal de 1 case, illisible pour démarrer un tracé depuis zéro.
 *
 * `centerX/centerZ` sont le milieu réel du tracé (`min`+`max`, pas juste `max`) —
 * un tracé posé loin de l'origine de la grille (ex. tracé minuscule au centre
 * d'une grille 25×25) a `minX/minY` très supérieurs à 0 ; ignorer `minX/minY`
 * pointait la caméra vers le coin (0,0) de la grille plutôt que vers le tracé
 * réel, qui pouvait finir hors du champ de la caméra (aperçu vide, tracé
 * jamais visible). Même logique pour `span`, qui doit refléter la taille du
 * tracé (`max-min`) et non sa distance à l'origine (`max`).
 */
export function computeTrackSceneBounds(
  allTiles: Track3dTile[],
  fallbackGrid?: { width: number; height: number }
): TrackSceneBounds {
  const xs = allTiles.map((t) => t.x);
  const ys = allTiles.map((t) => t.y);
  const minX = allTiles.length > 0 ? Math.min(...xs) : 0;
  const minY = allTiles.length > 0 ? Math.min(...ys) : 0;
  const maxX =
    allTiles.length > 0 ? Math.max(minX + 1, ...xs) : Math.max(1, (fallbackGrid?.width ?? 2) - 1);
  const maxY =
    allTiles.length > 0 ? Math.max(minY + 1, ...ys) : Math.max(1, (fallbackGrid?.height ?? 2) - 1);
  return {
    minX,
    minY,
    maxX,
    maxY,
    centerX: ((minX + maxX) * TILE_SIZE) / 2,
    centerZ: ((minY + maxY) * TILE_SIZE) / 2,
    span: Math.max(maxX - minX, maxY - minY, 1) * TILE_SIZE,
  };
}

/**
 * Taille du sol lointain : doit dépasser largement `OrbitControls.maxDistance`
 * (~span×3) sinon le rectangle d'herbe se découpe net sur le ciel au dézoom.
 */
export function trackDistantGroundSize(bounds: TrackSceneBounds): number {
  return Math.max(bounds.span * 14, 96);
}

/**
 * Position monde de la DirectionalLight / disque solaire.
 * Opposée au cadrage caméra défaut (`+0.7x / +0.9z`) et assez basse sur l'horizon
 * (~30–40°) pour rester dans la bande de ciel visible en vue piste / dézoom.
 */
export function trackSunLightPosition(bounds: TrackSceneBounds): {
  x: number;
  y: number;
  z: number;
} {
  const { centerX, centerZ, span } = bounds;
  return {
    x: centerX - (span * 1.2 + 6),
    y: span * 0.65 + 8,
    z: centerZ - (span * 1.35 + 6),
  };
}

/** Ciel, brouillard, éclairage et sol lointain — indépendant des tuiles de route/herbe. */
export function setupTrackSceneAtmosphere(scene: THREE.Scene, bounds: TrackSceneBounds): void {
  const { centerX, centerZ, span } = bounds;
  scene.background = new THREE.Color(0x8fc7e8);
  // Fog avant le bord du sol : le vert fond dans le ciel sans ligne de coupe.
  scene.fog = new THREE.Fog(0x8fc7e8, span * 1.4, span * 5.5);

  scene.add(new THREE.HemisphereLight(0xbfe3ff, 0x4a5f46, 1.3));
  // Lumière d'appoint neutre : les pièces Kenney (couleurs vives, vertex colors) s'en
  // sortent avec juste le duo hémisphère/soleil, mais les modèles importés/générés
  // (ex. Hunyuan 3D, textures PBR plus sombres) ont besoin d'un remplissage uniforme
  // pour ne pas paraître sous-exposés selon l'orientation de leurs normales.
  scene.add(new THREE.AmbientLight(0xffffff, 0.6));
  const sun = new THREE.DirectionalLight(0xfff3d6, 1.6);
  const sunPos = trackSunLightPosition(bounds);
  sun.position.set(sunPos.x, sunPos.y, sunPos.z);
  sun.target.position.set(centerX, 0, centerZ);
  scene.add(sun.target);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.left = -span * 1.4;
  sun.shadow.camera.right = span * 1.4;
  sun.shadow.camera.top = span * 1.4;
  sun.shadow.camera.bottom = -span * 1.4;
  sun.shadow.camera.far = span * 4;
  scene.add(sun);

  // Sol lointain très large : au dézoom on ne doit plus voir le bord du rectangle.
  // Repeat modéré : les taches restent lisibles sans retomber dans le vert plat.
  const groundSize = trackDistantGroundSize(bounds);
  const grassTexture = createGrassAlbedoTexture();
  const groundRepeat = Math.max(4, Math.round(groundSize / 6));
  grassTexture.repeat.set(groundRepeat, groundRepeat);
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(groundSize, groundSize),
    new THREE.MeshStandardMaterial({ map: grassTexture, roughness: 1, metalness: 0 })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(centerX, -0.02, centerZ);
  ground.receiveShadow = true;
  scene.add(ground);
}

/** Sol en herbe (Kenney Nature Kit) autour du tracé, en instances pour rester léger. Fire-and-forget. */
export function loadTrackSceneGrass(
  scene: THREE.Scene,
  bounds: TrackSceneBounds,
  isDisposed: () => boolean
): void {
  loadModel(GRASS_TILE_URL)
    .then((grassScene) => {
      if (isDisposed()) return;
      let grassMesh: THREE.Mesh | null = null;
      grassScene.traverse((child) => {
        if (!grassMesh && child instanceof THREE.Mesh) grassMesh = child;
      });
      if (!grassMesh) return;
      const mesh: THREE.Mesh = grassMesh;
      // Map soft + bruit en coordonnées monde (pas de teinte par case → pas de
      // damier type Minecraft). Les instances ne font que poser la géométrie.
      const grassMap = createGrassAlbedoTexture();
      grassMap.repeat.set(2.5, 2.5);
      const grassMat =
        mesh.material instanceof THREE.MeshStandardMaterial
          ? mesh.material.clone()
          : new THREE.MeshStandardMaterial();
      grassMat.metalness = 0;
      grassMat.roughness = 1;
      grassMat.map = grassMap;
      grassMat.color.set(0xffffff);
      grassMat.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', `#include <common>\nvarying vec3 vGrassWorldPos;`)
          .replace(
            '#include <worldpos_vertex>',
            `#include <worldpos_vertex>\nvGrassWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`
          );
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', `#include <common>\nvarying vec3 vGrassWorldPos;`)
          .replace(
            '#include <color_fragment>',
            `#include <color_fragment>
            float g1 = sin(vGrassWorldPos.x * 0.55) * sin(vGrassWorldPos.z * 0.48);
            float g2 = sin(vGrassWorldPos.x * 0.18 + vGrassWorldPos.z * 0.21);
            float g3 = sin((vGrassWorldPos.x + vGrassWorldPos.z) * 0.09);
            float grassNoise = g1 * 0.045 + g2 * 0.035 + g3 * 0.03;
            diffuseColor.rgb *= (1.0 + grassNoise);`
          );
      };
      grassMat.customProgramCacheKey = () => 'grass-world-noise-v1';
      grassMat.needsUpdate = true;
      const minGx = bounds.minX - GRASS_MARGIN;
      const maxGx = bounds.maxX + GRASS_MARGIN;
      const minGy = bounds.minY - GRASS_MARGIN;
      const maxGy = bounds.maxY + GRASS_MARGIN;
      const cols = maxGx - minGx + 1;
      const rows = maxGy - minGy + 1;
      const count = cols * rows;
      const grassField = new THREE.InstancedMesh(mesh.geometry, grassMat, count);
      grassField.receiveShadow = true;
      const matrix = new THREE.Matrix4();
      let i = 0;
      for (let gx = minGx; gx <= maxGx; gx++) {
        for (let gy = minGy; gy <= maxGy; gy++) {
          matrix.makeTranslation((gx + 0.5) * TILE_SIZE, 0, (gy + 0.5) * TILE_SIZE);
          grassField.setMatrixAt(i++, matrix);
        }
      }
      grassField.instanceMatrix.needsUpdate = true;
      scene.add(grassField);
    })
    .catch(() => {
      /* Sol par défaut conservé si la tuile d'herbe échoue à charger (POC). */
    });
}

/**
 * Charge et pose les pièces de route/bordures/ligne de départ sur chaque tuile.
 * `target` reçoit les groupes de tuile (une scène complète, ou un sous-groupe
 * dédié aux tuiles de route pour permettre un rechargement partiel — voir
 * l'éditeur 3D, qui reconstruit ce sous-groupe à chaque case (re)groupée).
 */
export function loadTrackSceneRoad(
  target: THREE.Object3D,
  allTiles: Track3dTile[],
  isDisposed: () => boolean
): Promise<void> {
  const decorKeys = Object.keys(DEFAULT_TILE_SLUG) as DecorKey[];
  return fetchCircuitPieceCatalog()
    .catch(() => [] as CircuitPiece3dCatalogEntry[])
    .then((catalog) =>
      Promise.all(
        decorKeys.map((key) => loadModel(resolvePieceUrl(catalog, DEFAULT_TILE_SLUG[key])))
      ).then((loaded) => {
        // Pièces explicitement groupées par l'éditeur 3D (`Circuit.roadPieces`),
        // au-delà du modèle par défaut — chargées à la demande, une fois par slug.
        // Les virages groupés sont générés par calcul (voir plus bas,
        // `buildProceduralCorner`), pas chargés depuis un `.glb` : on les exclut
        // ici pour ne jamais tenter de résoudre un slug synthétique en fichier disque.
        const customSlugs = [
          ...new Set(allTiles.map((t) => t.pieceSlug).filter((slug): slug is string => !!slug)),
        ].filter((slug) => !isProceduralCornerSlug(slug));
        return Promise.all(
          customSlugs.map((slug) =>
            loadModel(resolvePieceUrl(catalog, slug)).then((model): [string, THREE.Object3D] => [
              slug,
              model,
            ])
          )
        ).then((customPairs) => ({ loaded, customPairs }));
      })
    )
    .then(({ loaded, customPairs }) => {
      if (isDisposed()) return;
      const byKey = {} as Record<DecorKey, THREE.Object3D>;
      decorKeys.forEach((key, i) => {
        byKey[key] = loaded[i];
      });
      // Pivot partagé par les pièces "ligne droite" groupées (roadStraightLong…,
      // voir plus bas `sharesRoadPivot`) — dérivé du modèle Kenney même si celui-ci
      // n'est plus utilisé comme rendu par défaut (remplacé par
      // `buildProceduralStraight` ci-dessous, même principe que les virages : plus
      // de dépendance à un modèle dont le trottoir ne colle pas exactement au bord
      // de case).
      const roadOffset = centerOffset(byKey.straight);

      // Ligne droite par défaut : générée par calcul, mêmes largeurs de chaussée/
      // bordure que le virage (`buildProceduralCorner`) pour un raccord sans
      // décrochement radial à la jonction virage/ligne droite. Trois variantes,
      // seule la couleur de bordure change :
      // - classique : fondue dans l'asphalte (pas de marquage particulier).
      // - "bumper" (case collée à un virage, chicane/« S ») : bordure blanche
      //   unie (le virage garde ses rayures rouge/blanc, la ligne droite non).
      // - case de départ : bordure jaune + marquage de grille de départ.
      const templates: Record<DecorKey, THREE.Object3D> = {
        straight: buildProceduralStraight(),
      };
      const straightBumperTemplate = buildProceduralStraight({ curbColor: 0xffffff });
      const startLineTemplate = buildProceduralStraight({ curbColor: 0xf6c000 });
      // Ligne droite du tour joker : même gabarit, bordure violette (même teinte
      // que le surlignage de l'outil d'édition, voir `circuit3dEditor.ts`
      // `JOKER_LAP_BRANCH_COLOR`) au lieu du blanc — distingue la branche
      // alternative du tracé principal une fois la géométrie posée.
      const jokerStraightTemplate = buildProceduralStraight({ curbColor: JOKER_LAP_CURB_COLOR });
      // Case peinte en terre (esthétique uniquement, voir `circuitSurfaceStyle.ts` et
      // `circuit3dEditor.ts`) : même gabarit qu'une ligne droite/un virage classique,
      // seule la couleur de la chaussée change — les rallycross mêlent toujours
      // bitume et terre sur un même tracé, jamais l'un OU l'autre pour tout le circuit.
      const earthStraightTemplate = buildProceduralStraight({ baseColor: SURFACE_FILL_HEX.earth });
      const roadEdgeHalfWidth = PROCEDURAL_ROAD_WIDTH / 2;
      const straightSurfaceY = new THREE.Box3().setFromObject(templates.straight).max.y;
      templates.straight.add(buildSideLines(straightSurfaceY, roadEdgeHalfWidth));
      straightBumperTemplate.add(buildSideLines(straightSurfaceY, roadEdgeHalfWidth));
      jokerStraightTemplate.add(buildSideLines(straightSurfaceY, roadEdgeHalfWidth));
      earthStraightTemplate.add(buildSideLines(straightSurfaceY, roadEdgeHalfWidth));
      const startSurfaceY = new THREE.Box3().setFromObject(startLineTemplate).max.y;
      startLineTemplate.add(buildStartGridMarking(startSurfaceY + 0.005));

      // Virage par défaut (case non groupée) : généré par calcul, arm=0 (la
      // courbe est tangente aux 4 côtés de sa propre case, sans dépasser dans
      // les voisines) — remplace l'ancien `roadCornerSmall.glb`.
      const cornerTemplate: THREE.Object3D = buildProceduralCorner(0);
      const earthCornerTemplate: THREE.Object3D = buildProceduralCorner(0, {
        baseColor: SURFACE_FILL_HEX.earth,
      });

      // Pièces personnalisées : une ligne droite groupée (posée via le picker
      // générique) dont la géométrie dépasse 1×1 case partage le MÊME pivot
      // Kenney que la case de base (mesuré par bounding-box : roadStraightLong
      // s'étend depuis exactement la même origine que roadStraight, sans
      // redécalage) — la recentrer indépendamment casserait son alignement avec
      // les cases voisines qu'elle couvre (voir `pieceCovered` dans
      // track3dLayout.ts). Une pièce 1-case ordinaire (roadCrossing, roadBump…)
      // n'a pas cette contrainte de pivot partagé : centrage indépendant, comme avant.
      const customTemplates = new Map<string, THREE.Object3D>();
      for (const [slug, model] of customPairs) {
        const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3());
        const sharesRoadPivot = size.x > TILE_SIZE * 1.01 || size.z > TILE_SIZE * 1.01;
        const offset = sharesRoadPivot ? roadOffset.clone() : centerOffset(model);
        customTemplates.set(slug, anchorWithOffset(model, offset));
      }
      // Virages groupés (Petit/Moyen/Large) : un seul rayon possible par
      // palier, calculé une fois — pas besoin d'attendre un chargement réseau.
      // Variante terre par palier également précalculée (clé `${slug}::earth`) :
      // contrairement aux vraies pièces custom (modèle `.glb` figé), un virage
      // groupé reste généré par calcul comme le virage par défaut, donc tout
      // aussi peignable (repéré en testant : la terre restait invisible sur un
      // virage groupé, la pièce custom l'emportant toujours sur la couleur).
      const proceduralCornerEarthKey = (slug: string): string => `${slug}::earth`;
      for (const tile of allTiles) {
        const arm = tile.pieceSlug ? proceduralCornerArmFromSlug(tile.pieceSlug) : null;
        if (arm === null) continue;
        const slug = proceduralCornerSlug(arm);
        if (!customTemplates.has(slug)) customTemplates.set(slug, buildProceduralCorner(arm));
        const earthSlug = proceduralCornerEarthKey(slug);
        if (!customTemplates.has(earthSlug)) {
          customTemplates.set(
            earthSlug,
            buildProceduralCorner(arm, { baseColor: SURFACE_FILL_HEX.earth })
          );
        }
      }

      // `castShadow: false` sur les pièces de route elles-mêmes (seul
      // `receiveShadow` reste actif, pour les vraies ombres portées par les
      // voitures/le décor) : des cases coplanaires adjacentes qui s'ombragent
      // l'une l'autre produisent un artefact d'auto-ombrage classique (jointure
      // visible pile à la limite entre deux cases) — invisible sur un bitume
      // uniforme, mais devenu flagrant à la jonction d'un dégradé terre/bitume
      // (repéré en testant, voir la capture d'écran d'une case fondue).
      const enableShadows = (obj: THREE.Object3D): THREE.Object3D => {
        obj.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = false;
            child.receiveShadow = true;
          }
        });
        return obj;
      };

      for (const tile of allTiles) {
        // Case couverte par une pièce groupée voisine mais pas son ancre : le
        // modèle de l'ancre s'étend déjà visuellement par-dessus (même pivot
        // partagé, voir plus haut) — un rendu ici ferait doublon/chevaucherait.
        if (tile.pieceCovered) continue;
        const tileGroup = createTileGroup(tile);
        target.add(tileGroup);

        const proceduralArm = tile.pieceSlug ? proceduralCornerArmFromSlug(tile.pieceSlug) : null;
        const customTemplate = tile.pieceSlug
          ? proceduralArm !== null && tile.earth
            ? customTemplates.get(proceduralCornerEarthKey(tile.pieceSlug))
            : customTemplates.get(tile.pieceSlug)
          : undefined;
        const useBumperStraight = tile.kind === 'straight' && tile.curbed;
        // 'startMerge' (case de la ligne de départ la plus proche du circuit,
        // voir `track3dLayout.ts#buildStartLane3dTiles`) rendue à l'identique
        // de 'start' : même bordure jaune que le reste de la grille, pas de
        // traitement à part (l'ancienne bretelle évasée en sortie détonnait
        // avec le reste du tracé).
        // Une case peinte en terre prime sur les autres variantes de couleur de
        // bordure (bosse/tour joker) — au rallycross, une section terre n'a pas
        // le marquage blanc/violet d'une ligne droite bitumée, peu importe son
        // adjacence à un virage ou son appartenance au tour joker.
        //
        // Jonction terre/bitume (`earthFadeIn`/`earthFadeOut`) : construite à la
        // volée plutôt que clonée d'un template partagé — le dégradé est propre
        // à cette case précise (ses voisins), pas réutilisable ailleurs sur le
        // tracé (voir `createSurfaceFadeTexture`). Seules les lignes droites
        // sans pièce custom en profitent ; un virage garde une coupure nette
        // (géométrie en arc, fondu hors scope pour l'instant).
        let roadInstance: THREE.Object3D;
        if (customTemplate) {
          roadInstance = enableShadows(customTemplate.clone(true));
        } else if (tile.kind === 'start' || tile.kind === 'startMerge') {
          roadInstance = enableShadows(startLineTemplate.clone(true));
        } else if (tile.kind === 'corner') {
          roadInstance = enableShadows(
            (tile.earth ? earthCornerTemplate : cornerTemplate).clone(true)
          );
        } else if (tile.kind === 'straight' && (tile.earthFadeIn || tile.earthFadeOut)) {
          const baseColor = tile.earth ? SURFACE_FILL_HEX.earth : PROCEDURAL_ASPHALT_COLOR;
          const neighborColor = tile.earth ? PROCEDURAL_ASPHALT_COLOR : SURFACE_FILL_HEX.earth;
          const fadeInstance = buildProceduralStraight({
            baseColor,
            curbColor: null,
            fadeToStart: tile.earthFadeIn ? neighborColor : null,
            fadeToEnd: tile.earthFadeOut ? neighborColor : null,
          });
          fadeInstance.add(buildSideLines(straightSurfaceY, roadEdgeHalfWidth));
          roadInstance = enableShadows(fadeInstance);
        } else if (tile.earth) {
          roadInstance = enableShadows(earthStraightTemplate.clone(true));
        } else if (tile.jokerLap) {
          roadInstance = enableShadows(jokerStraightTemplate.clone(true));
        } else if (useBumperStraight) {
          roadInstance = enableShadows(straightBumperTemplate.clone(true));
        } else {
          roadInstance = enableShadows(templates.straight.clone(true));
        }
        if (tile.kind === 'straight' || tile.kind === 'start' || tile.kind === 'startMerge') {
          roadInstance.rotation.y = STRAIGHT_EXTRA_ROTATION;
        }
        // Virages (par défaut et groupés Petit/Moyen/Large) : aucune rotation
        // supplémentaire à poser ici. `buildProceduralCorner` est construit
        // nativement pour les bras Sud+Ouest ; combinée à la seule rotation de
        // `tileGroup` (déjà déterminée par `rotationSteps`), elle retombe
        // exactement sur les bras réels pour les 4 valeurs de `rotationSteps`
        // (vérifié par le calcul ET par mesure pixel par pixel sur un tracé
        // à 4 virages droite — voir la session de debug). L'ancien correctif
        // `CORNER_EXTRA_ROTATION` ne s'appliquait qu'aux modèles Kenney
        // "Large"/"Larger", dont l'orientation native (est+nord) différait.
        tileGroup.add(roadInstance);
      }
    })
    .catch(() => {
      /* Tracé non affiché si un modèle échoue à charger (POC, non bloquant). */
    });
}

/**
 * Charge et pose le décor 3D Kenney hors tracé (`Circuit.decor3d` — tribunes,
 * tentes, panneaux, lampadaires, etc.). Chaque pièce est centrée sur la boîte
 * englobante de son emprise (`decor.cells`, en cases de grille absolues) plutôt
 * que redimensionnée : les modèles Kenney sont déjà à l'échelle d'une case, une
 * empreinte multi-case ne fait qu'indexer plusieurs cases occupées au sol, pas
 * un modèle à étirer.
 */
export function loadTrackSceneDecor(
  target: THREE.Object3D,
  circuit: Circuit,
  isDisposed: () => boolean
): Promise<void> {
  const items = circuit.decor3d ?? [];
  if (items.length === 0) return Promise.resolve();

  return fetchCircuitPieceCatalog()
    .catch(() => [] as CircuitPiece3dCatalogEntry[])
    .then((catalog) => {
      const slugs = [...new Set(items.map((d) => d.modelId))];
      const carModelSlugs = new Set(
        catalog.filter((entry) => entry.carModel).map((entry) => entry.slug)
      );
      return Promise.all(
        slugs.map((slug) =>
          loadModel(resolvePieceUrl(catalog, slug)).then((model): [string, THREE.Object3D] => [
            slug,
            model,
          ])
        )
      ).then((pairs) => ({ pairs, carModelSlugs }));
    })
    .then(({ pairs, carModelSlugs }) => {
      if (isDisposed()) return;
      const templates = new Map<string, THREE.Object3D>();
      for (const [slug, model] of pairs) {
        if (carModelSlugs.has(slug)) model.scale.setScalar(computeVehicleModelScale(model));
        templates.set(slug, anchorWithOffset(model, centerOffset(model)));
      }

      for (const decor of items) {
        const template = templates.get(decor.modelId);
        if (!template || decor.cells.length === 0) continue;
        const minX = Math.min(...decor.cells.map((c) => c.x));
        const maxX = Math.max(...decor.cells.map((c) => c.x));
        const minY = Math.min(...decor.cells.map((c) => c.y));
        const maxY = Math.max(...decor.cells.map((c) => c.y));

        const group = new THREE.Group();
        group.position.set(
          ((minX + maxX + 1) / 2) * TILE_SIZE,
          0,
          ((minY + maxY + 1) / 2) * TILE_SIZE
        );
        group.rotation.y = -(decor.rotation * Math.PI) / 2;

        const instance = template.clone(true);
        instance.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        group.add(instance);
        target.add(group);
      }
    })
    .catch(() => {
      /* Décor non affiché si un modèle échoue à charger (POC, non bloquant). */
    });
}

/**
 * Charge et pose le décor en placement libre (`Circuit.freeDecor`, éditeur 3D) —
 * même chargement de modèle que `loadTrackSceneDecor`, mais positionné au point
 * flottant exact (`entry.x/entry.y`, pas de boîte englobante sur une liste de
 * cases : il n'y en a pas) et orienté en degrés libres plutôt qu'à 90°.
 */
export function loadFreeTrackSceneDecor(
  target: THREE.Object3D,
  circuit: Circuit,
  isDisposed: () => boolean
): Promise<void> {
  const items = circuit.freeDecor ?? [];
  if (items.length === 0) return Promise.resolve();

  return fetchCircuitPieceCatalog()
    .catch(() => [] as CircuitPiece3dCatalogEntry[])
    .then((catalog) => {
      const slugs = [...new Set(items.map((d) => d.modelId))];
      const carModelSlugs = new Set(
        catalog.filter((entry) => entry.carModel).map((entry) => entry.slug)
      );
      return Promise.all(
        slugs.map((slug) =>
          loadModel(resolvePieceUrl(catalog, slug)).then((model): [string, THREE.Object3D] => [
            slug,
            model,
          ])
        )
      ).then((pairs) => ({ pairs, carModelSlugs }));
    })
    .then(({ pairs, carModelSlugs }) => {
      if (isDisposed()) return;
      const templates = new Map<string, THREE.Object3D>();
      for (const [slug, model] of pairs) {
        if (carModelSlugs.has(slug)) model.scale.setScalar(computeVehicleModelScale(model));
        templates.set(slug, anchorWithOffset(model, centerOffset(model)));
      }

      for (const decor of items) {
        const template = templates.get(decor.modelId);
        if (!template) continue;

        const group = new THREE.Group();
        group.position.set(decor.x * TILE_SIZE, decor.elevation ?? 0, decor.y * TILE_SIZE);
        group.rotation.y = -(decor.rotationDeg * Math.PI) / 180;

        const instance = template.clone(true);
        instance.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        group.add(instance);
        target.add(group);
      }
    })
    .catch(() => {
      /* Décor non affiché si un modèle échoue à charger (POC, non bloquant). */
    });
}
